/**
 * Parking: the pad beside a building — a house's driveway (one slot) or a business's lot (two).
 * World-level so the generator can pave it and keep trees off it, and so the game and the
 * renderer agree on where it is (docs/DESIGN_PROPERTY.md).
 */
import { RF_SIDEWALK } from './generator';
import type { PlacedBuilding } from './buildings';
import { floorPlan } from './interior';

export function doorstep(b: PlacedBuilding, seed?: number): { x: number; y: number } {
  const plan = seed !== undefined ? floorPlan(b, 0, seed) : undefined;
  const d = plan?.doors[0];
  if (d) return { x: b.rot === 1 ? d[0] - 1 : b.rot === 3 ? d[0] + 2 : d[0] + 0.5, y: b.rot === 0 ? d[1] + 2 : b.rot === 2 ? d[1] - 1 : d[1] + 0.5 };
  return { x: b.rot === 1 ? b.x - 1.5 : b.rot === 3 ? b.x + b.w + 1.5 : b.x + b.w / 2, y: b.rot === 0 ? b.y + b.h + 1.5 : b.rot === 2 ? b.y - 1.5 : b.y + b.h / 2 };
}

export const propertyKey = (b: PlacedBuilding): string => b.main ?? `${b.x},${b.y}`; // (owner, Sep 22) a wing of a designed house belongs to the house (world/buildings.ts `main`)

/** A house's driveway: a pad on the lot next to the house, flush with its street front, on the side away from the door. `x,y` top-left (integers), `cx,cy` where a car sits, `angle` its heading (nose to the street). */
/** Driveway pad: 3 tiles wide per car, up to 5 long (a sedan is 4.5). A house has one slot, a business two. */
export const DRIVEWAY_W = 3, DRIVEWAY_L = 5;
export interface Driveway { x: number; y: number; w: number; h: number; /** parking slots: where a car is put, nose to the street */ slots: { cx: number; cy: number; angle: number }[]; cx: number; cy: number; angle: number }

export function driveway(b: PlacedBuilding, seed?: number, flip = false): Driveway {
  const d = doorstep(b, seed);
  const n = isBusiness(b) ? 2 : 1, W = DRIVEWAY_W * n;
  const L = Math.min(DRIVEWAY_L, Math.max(4, b.rot % 2 === 0 ? b.h : b.w)); // long enough for a pickup, never past the back of the building
  if (b.rot % 2 === 0) {
    // front along x: the pad stands beside the building, left or right (away from the door; `flip` = the other side), reaching L tiles back from the front edge
    const right = (d.x < b.x + b.w / 2) !== flip;
    const x = right ? b.x + b.w : b.x - W;
    const y = b.rot === 0 ? b.y + b.h - L : b.y;
    const angle = b.rot === 0 ? Math.PI : 0;
    const slots = Array.from({ length: n }, (_, i) => ({ cx: x + DRIVEWAY_W * i + DRIVEWAY_W / 2, cy: y + L / 2, angle }));
    return { x, y, w: W, h: L, slots, ...slots[0] };
  }
  const below = (d.y < b.y + b.h / 2) !== flip;
  const y = below ? b.y + b.h : b.y - W;
  const x = b.rot === 3 ? b.x + b.w - L : b.x;
  const angle = b.rot === 3 ? Math.PI / 2 : -Math.PI / 2;
  const slots = Array.from({ length: n }, (_, i) => ({ cx: x + L / 2, cy: y + DRIVEWAY_W * i + DRIVEWAY_W / 2, angle }));
  return { x, y, w: L, h: W, slots, ...slots[0] };
}
/** Is there room for the pad? Not on the carriageway, not over a neighbour (trees and street furniture are cleared, buildings are not; a sidewalk in front is fine — that is the curb cut). */
export function drivewayFits(world: { buildingAt(x: number, y: number): PlacedBuilding | undefined; roadAt(x: number, y: number): unknown; roadFlags(x: number, y: number): number; loaded(x: number, y: number): boolean }, dw: Driveway): boolean {
  for (let y = dw.y; y < dw.y + dw.h; y++) for (let x = dw.x; x < dw.x + dw.w; x++) {
    if (!world.loaded(x + 0.5, y + 0.5)) return false;
    if (world.buildingAt(x + 0.5, y + 0.5) || (world.roadAt(x + 0.5, y + 0.5) && !(world.roadFlags(x + 0.5, y + 0.5) & RF_SIDEWALK))) return false;
  }
  return true;
}

const BUSINESS_TAGS = ['shop', 'supermarket', 'tuning', 'bar', 'restaurant', 'cafe', 'diner', 'fast_food', 'bakery', 'gas_station', 'pawn', 'dealer', 'gun_shop', 'hotel', 'motel', 'laundromat', 'cinema', 'casino', 'car_wash', 'deli', 'general_store', 'pharmacy', 'bike_shop', 'garage', 'workshop', 'charging', 'ev_charger', 'barber'];
/** A store you can own: it pays daily. (Banks, hospitals, police and the like are not for sale.) */
export function isBusiness(b: PlacedBuilding): boolean {
  const d = b.def;
  if (d.property === false || b.height <= 0) return false;
  if (d.stories > 3 && !(typeof d.property === 'object')) return false;
  const t = new Set(d.tags);
  if (['bank', 'hospital', 'clinic', 'police', 'government', 'school', 'church', 'temple', 'mosque'].some((x) => t.has(x))) return false;
  return BUSINESS_TAGS.some((x) => t.has(x));
}
